import {
  NamedElementFilter,
  NamedElementIssueSilence,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  Classification,
  ClassificationObjectType,
  NamedElement,
  SpecTargetObjectType,
  Suggestion,
} from '@fullstory/proto/fs/services/search-settings';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { BehaviorSignalType } from '@fullstory/recording-api';
import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../types';
import { AppBackendRequestActions } from '../appbackend';
import {
  ClassificationRulesRequestActions,
  classificationRulesSelectors,
} from '../classificationRules';
import { getOrgId } from '../fullstory/selectors';
import { IngestionSettingsRequestActions } from '../ingestionSettings';
import { useFsSpecActions } from '../specs';
import { WatchedElementRequestActions } from '../watchedElements';
import { NamedElementsRequestActions } from './actions';
import { NamedElementCaptureOptions } from './types';

interface CaptureOptionsUpdate {
  prev: NamedElementCaptureOptions;
  next: NamedElementCaptureOptions;
}

export interface IssueSilenceUpdate {
  prev: NamedElementIssueSilence | null | undefined;
  next: NamedElementIssueSilence | null | undefined;
}

export const useNamedElementActions = () => {
  const syncPlatformsToSelectors = true;
  const intl = useIntl();
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const fsSpecEnabled = flags.featureFlags().AppFsSpec;
  const { updateObjectWithFsSpec, trackSpecMappingUpdate } = useFsSpecActions();
  const classificationRulesByElementId = useSelector(
    classificationRulesSelectors.getClassificationRuleRecordsByElementId,
  );

  const save = useCallback(
    async (
      element: NamedElement,
      captureOptions: CaptureOptionsUpdate,
      silence: IssueSilenceUpdate | null,
      // These slugs need to be an exhaustive list of all slugs that should be associated with this element.
      // Not including an existing slug will result in it being removed from the element.
      // Pass null to not affect mappings.
      fsSpecSlugs: string[] | null,
      hasExistingIssue: boolean,
      classification: Classification | null,
    ) => {
      try {
        const rsp = await dispatch(
          NamedElementsRequestActions.setNamedElement(
            orgId,
            element,
            hasExistingIssue,
            '',
            syncPlatformsToSelectors,
          ),
        );
        const captureOptionReqs = buildCaptureOptionReqs(
          orgId,
          dispatch,
          rsp.namedElement?.namedElement?.namedElement,
          captureOptions,
        );
        await Promise.all(captureOptionReqs);
        if (!silence?.prev && silence?.next) {
          await dispatch(
            NamedElementsRequestActions.silenceNamedElement({
              orgId,
              namedElementId: element.id,
              expiresAt: silence.next.expiresAt,
            }),
          );
        } else if (silence?.prev && silence?.next) {
          // This is the case where we go from silencing to silencing. For example:
          // We first silence for 72 hours. Then we come back and decide we want to silence for 7 days.
          // We first need to remove the previous silence and stick in the new one.
          await dispatch(
            NamedElementsRequestActions.unsilenceNamedElement({
              orgId,
              namedElementId: element.id,
              issueSilenceId: silence.prev.id,
            }),
          );
          await dispatch(
            NamedElementsRequestActions.silenceNamedElement({
              orgId,
              namedElementId: element.id,
              expiresAt: silence.next.expiresAt,
            }),
          );
        } else if (silence?.prev && !silence?.next) {
          await dispatch(
            NamedElementsRequestActions.unsilenceNamedElement({
              orgId,
              namedElementId: element.id,
              issueSilenceId: silence.prev.id,
            }),
          );
        }
        if (fsSpecEnabled && fsSpecSlugs && rsp.namedElement?.namedElement?.namedElement?.id) {
          const mappings = await updateObjectWithFsSpec(
            rsp.namedElement?.namedElement?.namedElement?.id,
            SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_ELEMENT,
            fsSpecSlugs,
          );

          mappings.forEach(({ mapping }) => {
            if (mapping?.mapping) trackSpecMappingUpdate(mapping.mapping, 'UPDATE', 'MODAL');
          });
        }

        if (rsp.namedElement?.namedElement?.namedElement?.id) {
          const elementId = rsp.namedElement.namedElement.namedElement.id;
          const existingClassificationRecord = classificationRulesByElementId.get(elementId);
          const hasClassificationChanged =
            existingClassificationRecord?.classificationRule?.classification !== classification;
          if (hasClassificationChanged) {
            if (classification === null) {
              // Delete existing classification rule if clearing classification
              if (existingClassificationRecord?.classificationRule?.id) {
                await dispatch(
                  ClassificationRulesRequestActions.deleteClassificationRule(
                    orgId,
                    existingClassificationRecord?.classificationRule?.id,
                  ),
                );
              }
            } else {
              // Create or update classification rule
              await dispatch(
                ClassificationRulesRequestActions.setClassificationRule(orgId, {
                  id: existingClassificationRecord?.classificationRule?.id || '',
                  objectId: elementId,
                  classificationObjectType:
                    ClassificationObjectType.CLASSIFICATION_OBJECT_TYPE_ELEMENT,
                  classification,
                }),
              );
            }
          }
        }

        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Saved Element.',
            id: '0dEyb8',
          }),
        );

        return rsp;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to save Element.',
            id: 'Iw6TuN',
          }),
        );
      }
      return null;
    },
    [
      dispatch,
      orgId,
      syncPlatformsToSelectors,
      fsSpecEnabled,
      notifications,
      intl,
      updateObjectWithFsSpec,
      trackSpecMappingUpdate,
      classificationRulesByElementId,
    ],
  );

  const archive = useCallback(
    async (namedElementId: string) => {
      try {
        const response = await dispatch(
          NamedElementsRequestActions.archiveNamedElement(orgId, namedElementId, true),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Archived Element.',
            id: 'Ql8hLZ',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive Element.',
            id: 'BCacB0',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const restore = useCallback(
    async (namedElementId: string) => {
      try {
        const response = await dispatch(
          NamedElementsRequestActions.archiveNamedElement(orgId, namedElementId, false),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Restored Element.',
            id: 'NdYmnx',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore Element.',
            id: 'qilwdJ',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const archiveMulti = useCallback(
    async (namedElementIds: string[]) => {
      try {
        if (flags.featureFlags().AppUseMultiArchive && namedElementIds.length > 1) {
          await dispatch(
            NamedElementsRequestActions.archiveMultiNamedElements(orgId, namedElementIds, true),
          );
        } else {
          await Promise.all(
            namedElementIds.map(namedElementId =>
              dispatch(
                NamedElementsRequestActions.archiveNamedElement(orgId, namedElementId, true),
              ),
            ),
          );
        }
        notifications.success(
          intl.formatMessage(
            {
              defaultMessage: 'Archived {count, number} Elements.',
              id: 'KQNBjz',
            },
            { count: namedElementIds.length },
          ),
        );
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive Elements.',
            id: '2nOdC9',
          }),
        );
      }
    },
    [flags, notifications, intl, dispatch, orgId],
  );

  const restoreMulti = useCallback(
    async (namedElementIds: string[]) => {
      try {
        if (flags.featureFlags().AppUseMultiArchive && namedElementIds.length > 1) {
          await dispatch(
            NamedElementsRequestActions.archiveMultiNamedElements(orgId, namedElementIds, false),
          );
        } else {
          await Promise.all(
            namedElementIds.map(namedElementId =>
              dispatch(
                NamedElementsRequestActions.archiveNamedElement(orgId, namedElementId, false),
              ),
            ),
          );
        }
        notifications.success(
          intl.formatMessage(
            {
              defaultMessage: 'Restored {count, number} Elements.',
              id: 'tJ8Rga',
            },
            { count: namedElementIds.length },
          ),
        );
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore Elements.',
            id: '84NLrc',
          }),
        );
      }
    },
    [flags, notifications, intl, dispatch, orgId],
  );

  const archiveAll =
    (archive: boolean) =>
    async (
      filters: NamedElementFilter[],
      query: string,
      numRows: number,
      lastModifiedBefore: Date,
    ) => {
      try {
        await dispatch(
          AppBackendRequestActions.bulkArchiveNamedElements({
            orgId,
            archive,
            filters,
            query,
            lastUpdatedBefore: Timestamp.fromDate(lastModifiedBefore),
          }),
        );
        notifications.success(
          archive
            ? intl.formatMessage(
                {
                  defaultMessage: 'Archived {count, number} Elements.',
                  id: 'KQNBjz',
                },
                { count: numRows },
              )
            : intl.formatMessage(
                {
                  defaultMessage: 'Restored {count, number} Elements.',
                  id: 'tJ8Rga',
                },
                { count: numRows },
              ),
        );
      } catch (e) {
        notifications.error(
          archive
            ? intl.formatMessage({
                defaultMessage: 'Failed to archive Elements.',
                id: '2nOdC9',
              })
            : intl.formatMessage({
                defaultMessage: 'Failed to restore Elements.',
                id: '84NLrc',
              }),
        );
      }
    };

  const rejectSuggestion = async (suggestedElementId: string) => {
    const suggestionResp = await dispatch(
      NamedElementsRequestActions.getSuggestedElement({ orgId, suggestedElementId }),
    );
    const suggestion = suggestionResp.suggestion?.suggestion?.suggestion;
    if (suggestion) {
      const updatedStatus: Suggestion = {
        ...suggestion,
        status: Suggestion.Status.STATUS_USER_REJECTED,
      };

      await dispatch(
        NamedElementsRequestActions.setSuggestedElement({ orgId, suggestion: updatedStatus }),
      );
      notifications.success(
        intl.formatMessage({ defaultMessage: 'Suggestion rejected', id: 'aVGGgE' }),
      );
    } else {
      notifications.success(
        intl.formatMessage({ defaultMessage: 'Failed to reject suggestion', id: 'QZjNCB' }),
      );
    }
  };

  return {
    save,
    archive,
    restore,
    archiveMulti,
    restoreMulti,
    archiveAll: archiveAll(true),
    restoreAll: archiveAll(false),
    updateObjectWithFsSpec,
    rejectSuggestion,
  };
};

function buildCaptureOptionReqs(
  orgId: string,
  dispatch: AppDispatch,
  element: NamedElement | null | undefined,
  captureOptions?: CaptureOptionsUpdate,
): Promise<any>[] {
  if (!captureOptions || !element || !element.id) return [];
  const reqs: Promise<any>[] = [];
  if (!captureOptions.prev.watched && captureOptions.next.watched) {
    reqs.push(
      dispatch(
        WatchedElementRequestActions.create(orgId, {
          namedElementId: element.id,
          selector: '',
          label: '',
          notes: '',
        }),
      ),
    );
  } else if (captureOptions.prev.watched && !captureOptions.next.watched) {
    reqs.push(
      dispatch(
        WatchedElementRequestActions.delete(orgId, {
          namedElementId: element.id,
          selector: '',
          label: '',
          notes: '',
        }),
      ),
    );
  }

  const prevSignals: BehaviorSignalType[] = [];
  if (captureOptions.prev.rageClicksBlocked) prevSignals.push(BehaviorSignalType.SignalRageClick);
  if (captureOptions.prev.deadClicksBlocked) prevSignals.push(BehaviorSignalType.SignalDeadClick);

  const nextSignals: BehaviorSignalType[] = [];
  if (captureOptions.next.rageClicksBlocked) nextSignals.push(BehaviorSignalType.SignalRageClick);
  if (captureOptions.next.deadClicksBlocked) nextSignals.push(BehaviorSignalType.SignalDeadClick);

  if (!isEqual(prevSignals, nextSignals)) {
    if (prevSignals.length === 0) {
      reqs.push(
        dispatch(
          IngestionSettingsRequestActions.createBehaviorSignalSetting(orgId, {
            namedElementId: element.id,
            signals: nextSignals,
            selector: '',
            notes: '',
          }),
        ),
      );
    } else if (nextSignals.length === 0) {
      reqs.push(
        dispatch(
          IngestionSettingsRequestActions.deleteBehaviorSignalSettingByNamedElement(
            orgId,
            element.id,
          ),
        ),
      );
    } else {
      reqs.push(
        dispatch(
          IngestionSettingsRequestActions.updateBehaviorSignalSetting(
            orgId,
            { namedElementId: element.id, signals: nextSignals, selector: '', notes: '' },
            { namedElementId: element.id, signals: prevSignals, selector: '', notes: '' },
          ),
        ),
      );
    }
  }

  return reqs;
}
